<script lang="ts">
  import type { FieldProps } from '../Field/Field.types';
  import Field from '../Field/Field.vue';

  type Option = any;

  type SelectedOption = any;

  export interface InputOptionsProps extends FieldProps {
    /**
     * The current value inclusive of the input & select
     */
    modelValue?: { value: string; option?: SelectedOption };

    /**
     * Input type
     */
    type?: string extends 'button' | 'checkbox' | 'radio' | 'submit' ? never : string;

    /**
     * Prevents the Selected Option from being truncated, if true
     */
    noTruncate?: boolean;

    /**
     * Options for the select
     */
    options?: Option[];

    /**
     * Placeholder text for the input
     * **Note:** placeholders should be used to display examples; they should not be used as labels because they are not accessible as labels. If a real label cannot be used, use the `aria-label` attribute.
     */
    placeholder?: string;

    /**
     * If `options` are an object, this is what prop to use for display.
     */
    displayBy?: string;

    /**
     * Default field to track selected options by.
     */
    trackBy?: string;
  }
</script>

<script lang="ts" setup>
  import { ref, useAttrs, useCssModule, watch, watchEffect } from 'vue';

  import Input from '../Input/Input.vue';
  import Select from '../Select/Select.vue';

  defineOptions({
    name: 'll-input-options',
  });

  const props = withDefaults(defineProps<InputOptionsProps>(), {
    modelValue: () => ({ value: '', option: undefined }),
    noTruncate: false,
    options: () => [],
    type: 'text',
    placeholder: undefined,
    displayBy: undefined,
    trackBy: undefined,
  });

  const emit = defineEmits<{
    /**
     * Emitted when the model value changes
     */
    (
      e: 'update:model-value',
      v: { value?: string; option?: SelectedOption; isValueChange: boolean; type: 'input' | 'select' },
    ): void;
    /**
     * Emitted when either the input or select changes
     */
    (
      e: 'change',
      v: { value?: string; option?: SelectedOption; isValueChange: boolean; type: 'input' | 'select' },
    ): void;
  }>();

  const slots = defineSlots<{
    /**
     * Default slot for rendering a custom input field.
     */
    default: (props: { fieldId: string }) => void;
    /**
     * Hint slot for rendering a custom hint text.
     */
    hint: () => unknown;
  }>();

  const attrs = useAttrs();
  const classes = useCssModule();
  const internalInput = ref<string>();
  const isInputFocused = ref(false);
  const selectedOption = ref<Option | undefined>();

  // Input value changed
  function handleInput(val?: string | number) {
    internalInput.value = String(val);

    emit('update:model-value', {
      value: internalInput.value,
      option: selectedOption.value,
      isValueChange: true,
      type: 'input',
    });
  }

  // Input blurred
  function handleInputChange() {
    emit('change', {
      value: internalInput.value,
      option: selectedOption.value,
      isValueChange: true,
      type: 'input',
    });
  }

  function handleSelectChange(val?: Option) {
    selectedOption.value = val;

    emit('change', {
      value: internalInput.value,
      option: selectedOption.value,
      isValueChange: false,
      type: 'select',
    });
    emit('update:model-value', {
      value: internalInput.value,
      option: selectedOption.value,
      isValueChange: false,
      type: 'select',
    });
  }

  watchEffect(() => {
    if (!selectedOption.value) {
      selectedOption.value = props.options[0];
    }
  });

  watch(
    () => props.modelValue.value,
    () => {
      internalInput.value = props.modelValue.value;
    },
    { immediate: true },
  );

  watch(
    () => props.modelValue.option,
    () => {
      selectedOption.value = props.modelValue.option;
    },
    { immediate: true },
  );

  if (attrs.value) {
    throw new Error('ll-input-options: use :model-value or v-model instead of :value.');
  }

  if (attrs.onInput) {
    throw new Error('ll-input-options: use the @update:model-value event instead of @input');
  }
</script>

<template>
  <Field v-bind="props" class="stash-input-options" data-test="stash-input-options">
    <template #default="{ fieldId, labelId }">
      <div v-if="props.isReadOnly" class="flex h-input items-center text-sm">
        <span :id="fieldId" :aria-labelledby="labelId" class="h-min">
          {{ internalInput || 0 }} {{ selectedOption?.name }}
        </span>
      </div>
      <div v-else class="flex" :class="[classes['input-wrapper'], { [classes['has-error']]: !!props.errorText }]">
        <slot :field-id="fieldId">
          <Input
            :id="fieldId"
            class="stash-input-options__input"
            data-test="stash-input-options|input"
            :type="props.type"
            :model-value="internalInput"
            :disabled="props.isDisabled || props.disabled"
            :placeholder="props.placeholder"
            @change="handleInputChange"
            @update:model-value="handleInput"
            @blur="isInputFocused = false"
            @focus="isInputFocused = true"
          />
        </slot>

        <Select
          single
          hide-search
          prevent-empty
          class="stash-input-options__select min-w-20"
          data-test="stash-input-options|select"
          :class="classes.select"
          :no-truncate="noTruncate"
          :options="options"
          :model-value="selectedOption"
          :display-by="props.displayBy"
          :track-by="props.trackBy"
          :disabled="props.isDisabled || props.disabled"
          @update:model-value="handleSelectChange"
        />
      </div>
    </template>
    <template v-if="slots.hint?.()" #hint>
      <!-- @slot Hint slot for rendering text below the input -->
      <slot name="hint"></slot>
    </template>
  </Field>
</template>

<style module>
  .input-wrapper {
    & > div:has(input) {
      display: inline-block;
      flex: 1;
      margin-right: -1px;
    }

    & input {
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
    }

    &.has-error input,
    &.has-error input:hover:not(:focus),
    &.has-error :global(.stash-select__content),
    &.has-error :global(.stash-select__content:hover:not(:focus)) {
      border-color: var(--color-red-500);
    }

    & div:has(input:focus) {
      z-index: 200 !important;
    }
  }

  .select :global(.stash-select__content) {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }

  .select:global(.stash-select--active .stash-select__content) {
    min-width: 0;
  }
</style>
